import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { FileDown, CheckCircle } from "lucide-react";
import { formatCurrency } from "../../lib/currency";

const PayslipDialog = ({
  open,
  onOpenChange,
  payroll,
  getEmployeeName,
  onDownload,
  onProcess,
  currency,
}) => {
  const fmt = (amount) => formatCurrency(amount, currency);

  if (!payroll) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between">
            <span>Payslip Details</span>
            <Badge variant="outline" className={
              payroll.status === "pending"
                ? "bg-amber-50 text-amber-700"
                : "bg-green-50 text-green-700"
            }>
              {payroll.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Employee & Period */}
          <div className="p-3 bg-slate-50 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Employee</span>
              <span className="font-medium">{getEmployeeName(payroll.employee_id)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Period</span>
              <span className="font-medium">{payroll.month} {payroll.year}</span>
            </div>
            {payroll.working_days && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Working Days</span>
                <span className="font-medium">{payroll.present_days || 0}/{payroll.working_days}</span>
              </div>
            )}
            {payroll.lop_days > 0 && (
              <div className="flex justify-between text-sm text-blue-600">
                <span>LOP Days</span>
                <span className="font-medium">{payroll.lop_days}</span>
              </div>
            )}
          </div>

          {/* Earnings */}
          <div className="border rounded-lg p-3">
            <h4 className="font-medium text-sm text-slate-800 mb-2">Earnings</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Basic Salary</span><span>{fmt(payroll.basic_salary)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">HRA</span><span>{fmt(payroll.hra || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Conveyance</span><span>{fmt(payroll.conveyance_allowance || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Special Allowance</span><span>{fmt(payroll.special_allowance || 0)}</span></div>
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Gross Salary</span>
                <span className="text-green-600">{fmt(payroll.gross_salary)}</span>
              </div>
            </div>
          </div>

          {/* Deductions */}
          <div className="border rounded-lg p-3">
            <h4 className="font-medium text-sm text-slate-800 mb-2">Deductions</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">PF (Employee)</span><span className="text-red-600">-{fmt(payroll.pf_employee || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">ESI</span><span className="text-red-600">-{fmt(payroll.esi_employee || 0)}</span></div>
              {payroll.professional_tax > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">Professional Tax</span><span className="text-red-600">-{fmt(payroll.professional_tax)}</span></div>
              )}
              {payroll.tds > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">TDS</span><span className="text-red-600">-{fmt(payroll.tds)}</span></div>
              )}
              {payroll.advance_deduction > 0 && (
                <div className="flex justify-between"><span className="text-orange-600">Advance/Loan</span><span className="text-red-600">-{fmt(payroll.advance_deduction)}</span></div>
              )}
              {payroll.lop_amount > 0 && (
                <div className="flex justify-between"><span className="text-blue-600">LOP Deduction</span><span className="text-red-600">-{fmt(payroll.lop_amount)}</span></div>
              )}
              {payroll.custom_deductions?.map((d, i) => (
                <div key={`ded-${d.name}-${i}`} className="flex justify-between"><span className="text-slate-500">{d.name}</span><span className="text-red-600">-{fmt(d.amount)}</span></div>
              ))}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total Deductions</span>
                <span className="text-red-600">-{fmt(payroll.total_deductions || 0)}</span>
              </div>
            </div>
          </div>

          {/* Net Salary */}
          <div className="p-4 bg-gradient-to-r from-violet-50 to-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-slate-700">Net Salary</span>
              <span className="text-2xl font-bold text-slate-800">{fmt(payroll.net_salary)}</span>
            </div>
          </div>

          {/* Employer Contributions */}
          {(payroll.pf_employer > 0 || payroll.esi_employer > 0) && (
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <h4 className="font-medium text-slate-700 mb-2">Employer Contributions (for reference)</h4>
              <div className="flex justify-between"><span className="text-slate-500">PF Employer</span><span>{fmt(payroll.pf_employer || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">ESI Employer</span><span>{fmt(payroll.esi_employer || 0)}</span></div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onDownload(payroll.id)} data-testid="download-payslip-dialog-btn">
              <FileDown className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {payroll.status === "pending" && (
              <Button className="flex-1 btn-primary" onClick={() => onProcess(payroll.id)} data-testid="process-payslip-dialog-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Process
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayslipDialog;
